import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from './axiosConfig';
import './DeveloperDashboard.css';
import './DeveloperActivity.css';

const day = date => date.toISOString().slice(0, 10);
const initialFilters = () => ({ search: '', school_id: '', event: '',
  from: day(new Date(Date.now() - 30 * 86400000)), to: day(new Date()) });
const labels = { login: 'Signed in', logout: 'Signed out', app_access: 'App use', page_view: 'Page visit', feature_request: 'Feature request' };
const formatTime = value => new Intl.DateTimeFormat('en-ZA', {
  timeZone: 'Africa/Johannesburg', dateStyle: 'medium', timeStyle: 'medium',
}).format(new Date(`${value.replace(' ', 'T')}Z`));

export default function DeveloperActivity() {
  const [filters, setFilters] = useState(initialFilters);
  const [applied, setApplied] = useState(initialFilters);
  const [offset, setOffset] = useState(0);
  const [schools, setSchools] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [updated, setUpdated] = useState(null);
  useEffect(() => {
    let live = true;
    axios.get(`${process.env.REACT_APP_API_URL}/get_schools.php`)
      .then(result => { if (live) setSchools(result.data.schools || []); }).catch(() => {});
    return () => { live = false; };
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError('');
    axios.get(`${process.env.REACT_APP_API_URL}/developer_activity.php`, {
      params: { ...applied, offset }, signal: controller.signal,
    }).then(({ data: response }) => {
      if (!response.success) throw new Error(response.error || 'Could not load activity.');
      if (!controller.signal.aborted) { setData(response); setUpdated(new Date()); }
    }).catch(err => {
      if (!controller.signal.aborted) { setData(null); setError(err.response?.data?.error || err.message || 'Could not load activity.'); }
    }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [applied, offset, refresh]);
  const change = event => setFilters({ ...filters, [event.target.name]: event.target.value });
  const apply = event => { event.preventDefault(); setOffset(0); setApplied({ ...filters }); };
  const reset = () => { const next = initialFilters(); setFilters(next); setApplied(next); setOffset(0); };
  const summary = data?.summary;
  return <main className="developer-dashboard developer-activity">
    <header className="developer-hero">
      <div><p>Developer access only</p><h1>User activity</h1><span>See who signed in and how people use MarkSure.</span></div>
      <Link className="developer-button developer-button--quiet" to="/developer">Control centre</Link>
    </header>
    <section className="developer-workspace">
      <div className="toolbar"><div><h2>Activity history</h2><p>Times are shown in South African time (SAST). Date filters use UTC.</p></div>
        <button className="developer-button" disabled={loading} onClick={() => setRefresh(value => value + 1)}>{loading ? 'Loading...' : 'Refresh activity'}</button>
      </div>
      <form className="activity-filters" onSubmit={apply}>
        <label>User or school<input name="search" value={filters.search} onChange={change} placeholder="Search by name" maxLength={100} /></label>
        <label>School<select name="school_id" value={filters.school_id} onChange={change}><option value="">All schools</option>{schools.map(school => <option key={school.id} value={school.id}>{school.school_name}</option>)}</select></label>
        <label>Activity<select name="event" value={filters.event} onChange={change}><option value="">All activity</option>{Object.entries(labels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
        <label>From (UTC)<input type="date" name="from" value={filters.from} max={filters.to} onChange={change} required /></label>
        <label>To (UTC)<input type="date" name="to" value={filters.to} min={filters.from} onChange={change} required /></label>
        <div className="activity-filter-actions"><button className="developer-button" type="submit">Apply filters</button><button className="developer-button developer-button--quiet" type="button" onClick={reset}>Reset</button></div>
      </form>
      <div className="summary-grid" aria-label="Activity totals for current filters">
        <article><span>Users active in period</span><strong>{summary?.users ?? '-'}</strong></article>
        <article><span>Sign-ins in period</span><strong>{summary?.logins ?? '-'}</strong></article>
        <article><span>Recorded events</span><strong>{summary?.events ?? '-'}</strong></article>
      </div>
      <p className="activity-explanation">History starts when activity tracking is installed. App use is sampled at most once every five minutes per user. Feature requests show that a tool was requested; they do not confirm completion.</p>
      {error && <p role="alert" className="activity-error">{error} <button onClick={() => setRefresh(value => value + 1)}>Try again</button></p>}
      <div className="activity-table-wrap" aria-busy={loading}>
        <table className="activity-table"><thead><tr><th>When (SAST)</th><th>User</th><th>School</th><th>Activity</th><th>Details</th></tr></thead>
          <tbody>{!loading && data?.events.map(event => <tr key={event.id}>
            <td><time dateTime={`${event.created_at.replace(' ', 'T')}Z`}>{formatTime(event.created_at)}</time></td>
            <td><strong>{event.username}</strong><small>{event.user_role}</small></td>
            <td>{event.school_name}</td><td><span className={`activity-badge activity-badge--${event.event_type}`}>{labels[event.event_type] || event.event_type}</span></td>
            <td>{event.description}</td>
          </tr>)}</tbody>
        </table>
        {loading && <p className="empty-note" role="status">Loading user activity...</p>}
        {!loading && !error && data?.events.length === 0 && <p className="empty-note">No activity matches these filters. New activity will appear as people use the app.</p>}
      </div>
      <div className="activity-pagination"><span>{data && !loading ? `${data.summary.events ? offset + 1 : 0}-${offset + data.events.length} of ${data.summary.events} events` : ''}{updated && !loading ? ` · Updated ${updated.toLocaleTimeString()}` : ''}</span>
        <div><button className="developer-button developer-button--quiet" disabled={loading || offset === 0} onClick={() => setOffset(value => Math.max(0, value - 50))}>Previous</button><button className="developer-button developer-button--quiet" disabled={loading || !data?.has_more} onClick={() => setOffset(value => value + 50)}>Next</button></div>
      </div>
    </section>
  </main>;
}
